import { computed, defineComponent, reactive, useContext, useCssModule, useRoute } from '@nuxtjs/composition-api';
import { useTask } from 'vue-concurrency';
import { useBrandvoices, useSearch } from '~/composables';
import { getArticleInfo, getPubSearch } from '~/utils';
import { useDuck } from '~/services';
export default defineComponent({
    name: 'ArchiveSearchResults',
    props: {
        isLoading: {
            default: false,
            type: Boolean,
        },
    },
    setup(props, { root }) {
        /**
         * Composables
         */
        const brandvoices = useBrandvoices();
        const context = useContext();
        const css = useCssModule();
        const duck = useDuck();
        const route = useRoute();
        const search = useSearch();
        /**
         * DayJS
         */
        const dayjs = context.$dayjs;
        /**
         * Is loading
         */
        const isCurrentLoading = computed(() => props.isLoading || search.isLoading);
        /**
         * Total title
         */
        const totalTitle = computed(() => {
            return search.completedQuery ? 'Найдено' : 'Всего';
        });
        /**
         * Current articles
         */
        const currentArticles = computed(() => {
            return search.articles.map((article) => {
                return {
                    ...article,
                    ...getArticleInfo.call(root, article, { shouldGetTags: false }),
                    publishedAt: dayjs(article?.time * 1000).format(search.dateFormat),
                };
            });
        });
        /**
         * Pagination
         */
        const pagination = reactive({
            current: 1,
            limit: search.limit,
            offset: computed(() => pagination.limit * (pagination.current - 1)),
            total: computed(() => Math.ceil(search.total / search.limit)),
        });
        /**
         * Search task
         */
        const searchTask = useTask(function* () {
            try {
                search.set('error', '');
                search.set('isLoading', true);
                const { data: articles, headers } = yield getPubSearch({
                    brandvoices,
                    duck,
                    route: route.value,
                }, {
                    'list[limit]': pagination.limit,
                    'list[offset]': pagination.offset,
                    ...(search.query && { 'node[title]': search.query }),
                    ...(search.dateRange.length && {
                        'node[sort]': search.backendSortType,
                        'node[start]': String(dayjs(search.dateRange[0], search.dateFormat).unix()),
                        'node[end]': String(dayjs(search.dateRange[1], search.dateFormat)
                            .startOf('day')
                            .add(23, 'hour')
                            .add(59, 'minute')
                            .add(59, 'second')
                            .unix()),
                    }),
                });
                if (!articles.length) {
                    search.set('notFound', true);
                    search.set('total', 0);
                    return;
                }
                search.set('articles', articles);
                const total = Number(headers?.get('x-total-count')) || 0;
                search.set('total', total);
            }
            catch (error) {
                console.error(error);
                search.set('error', error);
            }
            finally {
                search.set('isLoading', false);
            }
        });
        function handlePagination(current) {
            pagination.current = current;
            searchTask.perform();
        }
        function handleSort() {
            let sortType = search.get('sortType');
            if (!sortType) {
                return search.set('sortType', 'ascending');
            }
            sortType = sortType === 'ascending' ? 'descending' : 'ascending';
            search.set('sortType', sortType);
            searchTask.perform();
        }
        return {
            currentArticles,
            css,
            handlePagination,
            handleSort,
            isCurrentLoading,
            pagination,
            search,
            searchTask,
            totalTitle,
        };
    },
});
